import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { identifyClarityUser } from '../../lib/tracking/clarity';
import { useAuthStore } from '../../stores/useAuthStore';

// 아직 인스타를 연결하지 않은 손님은 화면을 옮길 때 다시 확인하되, 너무 잦지 않게
const RECHECK_INTERVAL_MS = 15_000;

/**
 * 행동 분석 도구에 '이 녹화·이 기록은 어느 손님인지'를 알려 준다(화면에는 아무것도 그리지 않음).
 * 2026-10-22 대표 7844 "가리지마 절대 그러면 유저 누구인지 영영 알수없어" — 문서: Readme/BehaviorTracking.md
 * - 회원 번호 = 로그인한 손님(useAuthStore)
 * - 인스타 아이디 = 대시보드와 같은 기준(saved_instagram_accounts에서 updated_at 최신 1개)
 * - 로그인한 손님이 화면을 옮길 때마다 클래리티 identify를 다시 부른다.
 */
export function TrackingIdentity() {
    const { user } = useAuthStore();
    const location = useLocation();
    const [instagramUsername, setInstagramUsername] = useState<string | null>(null);
    const lookup = useRef<{ userId: string | null; found: boolean; checkedAt: number }>({ userId: null, found: false, checkedAt: 0 });

    const userId = user?.id ?? null;

    useEffect(() => {
        if (!userId) {
            lookup.current = { userId: null, found: false, checkedAt: 0 };
            setInstagramUsername(null);
            return;
        }
        if (lookup.current.userId !== userId) {
            lookup.current = { userId, found: false, checkedAt: 0 };
            setInstagramUsername(null);
        }
        if (lookup.current.found || Date.now() - lookup.current.checkedAt < RECHECK_INTERVAL_MS) return;
        lookup.current.checkedAt = Date.now();

        // 화면을 옮겨도 답은 버리지 않는다(다른 손님으로 바뀐 경우만 버림)
        void supabase
            .from('saved_instagram_accounts')
            .select('username')
            .eq('user_id', userId)
            .order('updated_at', { ascending: false })
            .limit(1)
            .then(({ data, error }) => {
                if (lookup.current.userId !== userId) return;
                if (error) {
                    console.warn('행동 분석 손님 이름 확인 실패:', error.message);
                    return;
                }
                const username = data?.[0]?.username ?? null;
                lookup.current.found = Boolean(username);
                setInstagramUsername(username);
            });
    }, [userId, location.pathname]);

    useEffect(() => {
        if (!userId) return;
        identifyClarityUser(userId, instagramUsername);
    }, [userId, instagramUsername, location.pathname, location.search]);

    return null;
}
